<template>
  <div class="sgp-backdrop" @click.self="$emit('close')">
    <div class="sgp-panel">
      <div class="sgp-header">
        <span class="sgp-title">Generar estado de cuenta</span>
        <button class="sgp-close" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
      </div>

      <div class="sgp-body">
        <span class="sgp-section-title">Periodo y alcance</span>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includePriorPeriods" /> Incluir saldos de periodos anteriores</label>

        <span class="sgp-section-title">Contenido a incluir</span>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includeServiceBreakdown" /> Desglose de servicios por concepto</label>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includeTicketReferences" /> Referencia de tickets de mantenimiento</label>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includePaymentHistory" /> Historial de pagos del periodo</label>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includeBankDetails" /> Datos bancarios para pago</label>
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includeLatePenaltyClause" /> Cláusula de penalidad por mora</label>

        <span class="sgp-section-title">Identidad del parque</span>
        <input v-model="config.rfc" class="sgp-input" placeholder="RFC del parque" />
        <input v-model="config.fiscalAddress" class="sgp-input" placeholder="Dirección fiscal" />
        <input v-model="config.phone" class="sgp-input" placeholder="Teléfono" />
        <input v-model="config.contactEmail" class="sgp-input" placeholder="Correo de contacto" />
        <label class="sgp-toggle-row"><input type="checkbox" v-model="options.includeLogo" /> Incluir logo del parque</label>

        <span class="sgp-section-title">Formato de entrega</span>
        <label class="sgp-radio-row"><input type="radio" value="DOWNLOAD" v-model="deliveryMode" /> Descargar PDF</label>
        <label class="sgp-radio-row"><input type="radio" value="EMAIL" v-model="deliveryMode" /> Enviar por correo</label>
        <template v-if="deliveryMode === 'EMAIL'">
          <input v-model="recipient" class="sgp-input" placeholder="Correo del contacto de pagos" />
          <input v-model="emailSubject" class="sgp-input" placeholder="Asunto del correo" />
        </template>
      </div>

      <div class="sgp-footer">
        <div v-if="errorMsg" class="sgp-notice" role="alert">{{ errorMsg }}</div>
        <button class="sgp-preview-btn" :disabled="generating" @click="onGenerate">{{ generating ? 'Generando...' : (deliveryMode === 'EMAIL' ? 'Enviar' : 'Descargar PDF') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { API_ORIGIN } from '../../config'
import { ref, onMounted } from 'vue'
import { getBillingConfig, saveBillingConfig, generateStatement } from '@/services/charges'

const props = defineProps({
  parkId: { type: String, required: true },
  contractId: { type: String, required: true },
  period: { type: String, required: true },
  defaultRecipient: { type: String, default: '' },
})

const emit = defineEmits(['close', 'generated'])

const options = ref({
  includePriorPeriods: true, includeServiceBreakdown: true, includeTicketReferences: true,
  includePaymentHistory: true, includeBankDetails: true, includeLatePenaltyClause: false, includeLogo: true,
})
const config = ref({ rfc: '', fiscalAddress: '', phone: '', contactEmail: '' })
const deliveryMode = ref('DOWNLOAD')
const recipient = ref(props.defaultRecipient)
const emailSubject = ref('')
const generating = ref(false)
const errorMsg = ref('')

onMounted(async () => {
  const c = await getBillingConfig(props.parkId)
  if (c) config.value = { ...config.value, ...c }
})

async function onGenerate() {
  if (!props.parkId) {
    console.error('parkId no definido', props)
    return
  }
  generating.value = true
  errorMsg.value = ''
  try {
    await saveBillingConfig(props.parkId, config.value)
    const result = await generateStatement(props.parkId, {
      parkId: props.parkId, contractId: props.contractId, period: props.period,
      options: options.value, deliveryMode: deliveryMode.value, recipient: recipient.value,
    })
    if (deliveryMode.value === 'DOWNLOAD') {
      window.open(`${API_ORIGIN}${result.downloadUrl}`, '_blank')
    }
    // Se generó el PDF pero no había a quién escribirle: se avisa y el panel sigue abierto para poner un correo.
    if (deliveryMode.value === 'EMAIL' && result.emailQueued === false) {
      errorMsg.value = 'El estado de cuenta se generó, pero no se envió: el inquilino no tiene un contacto de pagos con correo. Escribe un correo arriba o agrégalo en el contrato.'
      return
    }
    emit('generated', result)
  } catch (err) {
    errorMsg.value = err.message || 'No se pudo generar el estado de cuenta'
  } finally {
    generating.value = false
  }
}
</script>

<style scoped>
.sgp-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.3); display: flex; align-items: center; justify-content: center; z-index: 2000; }
.sgp-panel { width: 380px; max-height: 85vh; background: #ffffff; border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); display: flex; flex-direction: column; overflow: hidden; }
.sgp-header { display: flex; align-items: center; padding: 0.85rem 1.25rem; border-bottom: 1px solid #f1f5f9; }
.sgp-title { font-family: var(--font-display); font-weight: 700; font-size: 15px; color: #1e3a5f; }
.sgp-close { margin-left: auto; background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }
.sgp-body { flex: 1; min-height: 0; overflow-y: auto; padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.4rem; }
.sgp-body::-webkit-scrollbar { width: 6px; }
.sgp-body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.sgp-body::-webkit-scrollbar-track { background: transparent; }
.sgp-section-title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; color: #94a3b8; margin-top: 0.6rem; }
.sgp-toggle-row, .sgp-radio-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }
.sgp-input { font-family: var(--font-body); font-size: 12px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0.4rem 0.5rem; background: #f8f9fc; }
.sgp-footer { padding: 0.85rem 1.25rem; border-top: 1px solid #f1f5f9; }
.sgp-preview-btn { width: 100%; background: #3b82f6; color: #ffffff; border: none; border-radius: 6px; height: 38px; font-family: var(--font-body); font-size: 13px; font-weight: 500; cursor: pointer; }
.sgp-preview-btn:disabled { opacity: 0.6; }
.sgp-notice { font-family: var(--font-body); font-size: 12px; line-height: 1.4; border-radius: 6px; padding: 0.5rem 0.65rem; margin-bottom: 0.6rem; background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
</style>